/**
 * reportsHarvestInspectionsPeople sagas
 */
import {
    call,
    delay,
    put,
    race,
    select,
    take,
    takeLatest
} from 'redux-saga/effects';
import get from 'lodash.get';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    REPORTS_HARVEST_INSPECTIONS_PEOPLE_LOAD_REQUESTED,
    REPORTS_HARVEST_INSPECTIONS_PEOPLE_POLLING_REQUESTED,
    REPORTS_HARVEST_INSPECTIONS_PEOPLE_POLLING_CANCELED
} from './constants';

// Actions
import setHash from '../hashes';
import {
    succeededLoadReportsHarvestInspectionsPeople,
    failedLoadReportsHarvestInspectionsPeople,
    succeededPollingReportsHarvestInspectionsPeople,
    failedPollingReportsHarvestInspectionsPeople,
    setReportsHarvestInspectionsPeople
} from './actions';

// Selectors
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
import { makeSelectHash } from '../hashes/selectors';
import { selectIsOnline } from '../connection/selectors';
// HACK: disobeys folder structure rules
import { makeSelectParameters } from '../../scenes/Harvest/scenes/QualityReport/components/ReportsHarvestInspectionsPeopleRequest/selectors';

// Utils
import axiosRetry from '../../utils/axiosRetry';

// Environment
const REACT_APP_API_URL: string = String(process.env.REACT_APP_API_URL);

// Constants
const namespace = 'reportsHarvestInspectionsPeople';

type Config = ?{
    headers?: Object,
    params?: Object
};

export function* getReportsHarvestInspectionsPeople(
    config: Config
): Saga<void> {
    // Select Parameters
    const parameters = yield select(makeSelectParameters());

    const organizationId = yield select(selectCurrentOrganizationId);

    const url = `${REACT_APP_API_URL}/v1/Reports/harvestInspections/people`;

    return yield call(axiosRetry, {
        method: 'GET',
        url,
        ...(config || {}),
        params: {
            ...(config && config.params ? config.params : {}),
            organizationId,
            ...parameters
        }
    });
}

export function* loadReportsHarvestInspectionsPeople(): Saga<void> {
    try {
        const response = yield call(getReportsHarvestInspectionsPeople);

        const hash = get(response, 'headers.x-hash');

        // Succeeded Load Reports Harvest Inspections People
        yield put(succeededLoadReportsHarvestInspectionsPeople(response));

        // Set Reports Harvest Inspections People
        yield put(setReportsHarvestInspectionsPeople(response.data));

        // Set Hash
        yield put(setHash(namespace, hash));
    } catch (error) {
        yield put(failedLoadReportsHarvestInspectionsPeople(error));
    }
}

export function* watchReportsHarvestInspectionsPeopleLoadRequested(): Saga<void> {
    yield takeLatest(
        REPORTS_HARVEST_INSPECTIONS_PEOPLE_LOAD_REQUESTED,
        loadReportsHarvestInspectionsPeople
    );
}

export function* pollReportsHarvestInspectionsPeople(): Saga<void> {
    while (true) {
        yield delay(60000);

        // Select Is Online
        const isOnline = yield select(selectIsOnline);

        // Select Hash
        const hash = yield select(makeSelectHash(namespace));
        let hashMatch = false;

        if (isOnline) {
            try {
                if (hash) {
                    const hashResponse = yield call(
                        getReportsHarvestInspectionsPeople,
                        {
                            params: { hash: true }
                        }
                    );

                    hashMatch = hash === get(hashResponse, 'headers.x-hash');
                }
                if (!hashMatch) {
                    const response = yield call(
                        getReportsHarvestInspectionsPeople
                    );

                    const nextHash = get(response, 'headers.x-hash');

                    // Succeeded Polling Reports Harvest Inspections People
                    yield put(
                        succeededPollingReportsHarvestInspectionsPeople(
                            response
                        )
                    );

                    // Set Reports Harvest Inspections
                    yield put(
                        setReportsHarvestInspectionsPeople(response.data)
                    );

                    // Set Hash
                    yield put(setHash(namespace, nextHash));
                }
            } catch (error) {
                // Failed Polling Reports Harvest Inspections People
                yield put(failedPollingReportsHarvestInspectionsPeople(error));
            }
        }
    }
}

export function* watchReportsHarvestInspectionsPollingPeopleRequested(): Saga<void> {
    while (true) {
        const action = yield take(
            REPORTS_HARVEST_INSPECTIONS_PEOPLE_POLLING_REQUESTED
        );
        yield race([
            call(pollReportsHarvestInspectionsPeople, action),
            take(REPORTS_HARVEST_INSPECTIONS_PEOPLE_POLLING_CANCELED)
        ]);
    }
}
